// Enterprise Config Registry — Fase 14.4
export interface EnterpriseConfig {
  tv: { defaultSlideDurationMs: number; maxSlidesPerPlaylist: number; heartbeatIntervalMs: number; watchdogStallThresholdMs: number; reconnectBaseDelayMs: number; reconnectMaxDelayMs: number; snapshotIntervalMs: number; emergencyBroadcastTimeoutMs: number }
  twin: { pollIntervalMs: number; snapshotIntervalMs: number; maxActiveRisks: number; maxAutonomousActions: number; cooldownDefaultS: number; riskPropagationDepth: number }
  ai: { anomalyThreshold: number; predictionHorizonDays: number; insightCacheTtlMs: number; maxInsightsPerCycle: number }
  vision: { maxConcurrentStreams: number; frameBufferSize: number; ppeViolationCooldownMs: number; maxEventsPerMinute: number }
  observability: { logFlushIntervalMs: number; logFlushLimit: number; maxBufferSize: number; leakDetectorIntervalMs: number; healthMonitorIntervalMs: number; integrityMonitorIntervalMs: number; longSessionCheckIntervalMs: number }
  realtime: { reconnectBaseDelayMs: number; reconnectMaxDelayMs: number; maxReconnectAttempts: number; channelZombieThresholdMs: number }
  emergency: { sstRiskThreshold: number; ppeCriticalCount: number; offlineTimeoutMs: number; circuitBreakerThreshold: number }
}

const DEFAULTS: EnterpriseConfig = {
  tv: { defaultSlideDurationMs: 30_000, maxSlidesPerPlaylist: 20, heartbeatIntervalMs: 5_000, watchdogStallThresholdMs: 60_000, reconnectBaseDelayMs: 1_000, reconnectMaxDelayMs: 30_000, snapshotIntervalMs: 300_000, emergencyBroadcastTimeoutMs: 600_000 },
  twin: { pollIntervalMs: 60_000, snapshotIntervalMs: 300_000, maxActiveRisks: 500, maxAutonomousActions: 200, cooldownDefaultS: 600, riskPropagationDepth: 3 },
  ai: { anomalyThreshold: 0.7, predictionHorizonDays: 14, insightCacheTtlMs: 300_000, maxInsightsPerCycle: 10 },
  vision: { maxConcurrentStreams: 4, frameBufferSize: 30, ppeViolationCooldownMs: 30_000, maxEventsPerMinute: 100 },
  observability: { logFlushIntervalMs: 10_000, logFlushLimit: 50, maxBufferSize: 500, leakDetectorIntervalMs: 30_000, healthMonitorIntervalMs: 30_000, integrityMonitorIntervalMs: 60_000, longSessionCheckIntervalMs: 60_000 },
  realtime: { reconnectBaseDelayMs: 1_000, reconnectMaxDelayMs: 30_000, maxReconnectAttempts: 10, channelZombieThresholdMs: 600_000 },
  emergency: { sstRiskThreshold: 80, ppeCriticalCount: 3, offlineTimeoutMs: 30_000, circuitBreakerThreshold: 5 },
}

export interface ConfigValidationResult { valid: boolean; errors: string[]; warnings: string[] }

export function validateConfig(cfg: EnterpriseConfig): ConfigValidationResult {
  const errors: string[] = []; const warnings: string[] = []
  if (cfg.tv.defaultSlideDurationMs < 5_000) errors.push('tv.defaultSlideDurationMs must be >= 5000ms')
  if (cfg.tv.maxSlidesPerPlaylist < 1 || cfg.tv.maxSlidesPerPlaylist > 100) errors.push('tv.maxSlidesPerPlaylist must be 1-100')
  if (cfg.tv.watchdogStallThresholdMs < 10_000) warnings.push('tv.watchdogStallThresholdMs < 10s may cause false stalls')
  if (cfg.twin.pollIntervalMs < 5_000) errors.push('twin.pollIntervalMs must be >= 5000ms')
  if (cfg.ai.anomalyThreshold < 0 || cfg.ai.anomalyThreshold > 1) errors.push('ai.anomalyThreshold must be 0-1')
  if (cfg.emergency.sstRiskThreshold < 50 || cfg.emergency.sstRiskThreshold > 100) errors.push('emergency.sstRiskThreshold must be 50-100')
  return { valid: errors.length === 0, errors, warnings }
}

type DeepPartial<T> = { [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K] }

function deepMerge<T extends object>(base: T, override: DeepPartial<T>): T {
  const result = { ...base }
  for (const key of Object.keys(override) as Array<keyof T>) {
    const ov = override[key]
    if (ov !== undefined && typeof ov === 'object' && !Array.isArray(ov)) result[key] = deepMerge(base[key] as object, ov as DeepPartial<object>) as T[typeof key]
    else if (ov !== undefined) result[key] = ov as T[typeof key]
  }
  return result
}

class EnterpriseConfigRegistryClass {
  private _base: EnterpriseConfig = { ...DEFAULTS }
  private _companyOverrides = new Map<string, DeepPartial<EnterpriseConfig>>()
  private _envOverride: DeepPartial<EnterpriseConfig> | null = null
  private _cache = new Map<string, EnterpriseConfig>()

  applyEnvOverride(o: DeepPartial<EnterpriseConfig>): void { this._envOverride = o; this._cache.clear() }
  setCompanyOverride(id: string, o: DeepPartial<EnterpriseConfig>): void { this._companyOverrides.set(id, o); this._cache.delete(id) }

  getConfig(companyId?: string): EnterpriseConfig {
    const k = companyId ?? '__default__'
    if (this._cache.has(k)) return this._cache.get(k)!
    let cfg = this._base
    if (this._envOverride) cfg = deepMerge(cfg, this._envOverride)
    if (companyId && this._companyOverrides.has(companyId)) cfg = deepMerge(cfg, this._companyOverrides.get(companyId)!)
    const v = validateConfig(cfg)
    if (!v.valid) console.error('[EnterpriseConfig] Invalid config', k, v.errors)
    if (v.warnings.length > 0) console.warn('[EnterpriseConfig] Warnings', k, v.warnings)
    this._cache.set(k, cfg); return cfg
  }

  getDefaults(): EnterpriseConfig { return { ...DEFAULTS } }
  reset(): void { this._envOverride = null; this._companyOverrides.clear(); this._cache.clear(); this._base = { ...DEFAULTS } }
  getOverrideSummary() { return { env: this._envOverride !== null, companies: [...this._companyOverrides.keys()] } }
}

export const enterpriseConfig = new EnterpriseConfigRegistryClass()
export function getConfig(companyId?: string): EnterpriseConfig { return enterpriseConfig.getConfig(companyId) }
export { DEFAULTS as CONFIG_DEFAULTS }
